import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Tag, Typography, Table, Input, Space, Dropdown, Button, App, } from 'antd';
import { MoreOutlined, EditOutlined, KeyOutlined, LockOutlined, DeleteOutlined, } from '@ant-design/icons';
import { listClients, updateClientStatus, deleteClient, resendActivation,sendTemporaryPassword } from '../../services/clients';
import { SPLASH_ROLES_OPTIONS } from '../../utils/constants';
import { Select } from 'antd';
const { Title } = Typography;
const { useApp } = App;

const ClientsList = () => {
  const navigate = useNavigate();
  const { message, modal } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});
  const [search, setSearch] = useState('');
  const [roleId, setRoleId] = useState(null);

  const fetchData = useCallback(async (page, pageSize, srt, q, roleId) => {
    setLoading(true);
    try {
      const { items } = await listClients({
        page: page || pagination.current,
        pageSize: pageSize || pagination.pageSize,
        search: q || undefined,
        sortField: srt?.field,
        sortOrder: srt?.order,
        role: roleId || undefined,
      });
      setData(items.data || []);
      setPagination(prev => ({ 
        ...prev, 
        current: page || prev.current, 
        pageSize: pageSize || prev.pageSize, 
        total: items.total 
      }));
    } catch (err) {
      console.error(err);
      message.error('No se pudo cargar la lista de clientes.');
    } finally {
      setLoading(false);
    }
  }, [pagination, message]);

  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(pag.current, pag.pageSize, srt, search);
  };

  const onSearch = (value) => {
    setSearch(value || '');
    // al buscar, reinicia a página 1
    fetchData(1, pagination.pageSize, sorter, value || '');
  };

  const onChangeRole = (value) => {
    setRoleId(value || null);
    // al buscar, reinicia a página 1
    fetchData(1, pagination.pageSize, sorter, search, value === 'TODOS' ? null : value);
  };

      /**
   * Función para enviar contraseña temporal
   */
      const handleSendTemporaryPassword = async (record) => {
        modal.confirm({
          title: '¿Enviar contraseña temporal?',
          content: (
            <div>
              <p>Se enviará una contraseña temporal a:</p>
              <p style={{ fontWeight: 'bold' }}>{record.email}</p>
              <p style={{ color: '#fa8c16', marginTop: 8, fontSize: '12px' }}>
                ⚠️ El usuario recibirá un correo con la nueva contraseña
              </p>
            </div>
          ),
          okText: 'Enviar',
          cancelText: 'Cancelar',
          okButtonProps: {
            icon: <KeyOutlined />,
          },
          onOk: async () => {
            try {
      
              
              // Llamar al servicio con el email del usuario
              await sendTemporaryPassword({ email: record.email });
              
              message.success(`Contraseña temporal enviada a ${record.email}`);
              
              // Opcional: Refrescar datos si es necesario
              if (fetchData) {
                fetchData(pagination.current, pagination.pageSize, sorter, search);
              }
              
            } catch (error) {
              console.error('Error enviando contraseña temporal:', error);
              
              const errorMessage = error?.response?.data?.message 
                || error?.message 
                || 'Error al enviar la contraseña temporal';
              
              message.error(errorMessage);
            } finally {
            }
          },
        });
      };

  // Función para manejar las acciones del menú
  const handleMenuAction = (action, record) => {
    console.log('Acción:', action, 'Cliente:', record);
    
    switch (action) {
      case 'view':
        navigate(`/clients/view/${record.id}`);
        break;
      case 'edit':
        if (record.role === 'ESPECIALISTA') {
          navigate(`/clients/specialist/${record.id}/edit`);
        } else if (record.role === 'PACIENTE') {
          navigate(`/clients/patient/${record.id}/edit`);
        }
        break;
      case 'changePassword':
        handleSendTemporaryPassword(record);
        break;
      case 'toggle':
        const willActivate = !record.isActive;
        modal.confirm({
          title: willActivate ? '¿Activar cliente?' : '¿Desactivar cliente?',
          content: `${record.name || record.email || 'Usuario'}`,
          okText: willActivate ? 'Activar' : 'Desactivar',
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await updateClientStatus(record.id, willActivate);
              message.success(willActivate ? 'Cliente activado' : 'Cliente desactivado');
              fetchData(pagination.current, pagination.pageSize, sorter, search);
            } catch {
              message.error('No se pudo actualizar el estatus');
            }
          },
        });
        break;
      case 'delete':
        modal.confirm({
          title: '¿Eliminar usuario?',
          content: `${record.name || record.email || 'Cliente'}`,
          okText: 'Eliminar',
          okButtonProps: { danger: true },
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await deleteClient(record.id);
              message.success('Cliente eliminado');

              const nextPage = (data.length === 1 && pagination.current > 1)
                ? pagination.current - 1
                : pagination.current;
              setPagination(prev => ({ ...prev, current: nextPage }));
              fetchData(nextPage, pagination.pageSize, sorter, search);
            } catch {
              message.error('No se pudo eliminar el cliente');
            }
          },
        });
        break;
      case 'resendInvitation':
        modal.confirm({
          title: '¿Reenviar invitación?',
          content: `Se enviará un correo de activación a ${record.email || 'el cliente'}`,
          okText: 'Reenviar',
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await resendActivation(record.id);
              message.success('Invitación reenviada correctamente');
            } catch {
              message.error('No se pudo reenviar la invitación');
            }
          },
        });
        break;
      default:
        break;
    }
  };

  const getActionsMenu = (record) => {
    const menuItems = [
      /*
      {
        key: 'view',
        icon: <EyeOutlined />,
        label: 'Ver cliente',
      },
      */

      {
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Editar cliente',
      },
      {
        key: 'changePassword',
        icon: <KeyOutlined />,
        label: 'Cambiar contraseña',
      },
      {
        type: 'divider',
      },
      {
        key: 'toggle',
        icon: <LockOutlined />,
        label: record.isActive ? 'Desactivar' : 'Activar',
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Borrar cliente',
        danger: true,
      }
    ];

    return {
      items: menuItems,
      onClick: ({ key }) => handleMenuAction(key, record),
    };
  };

  const columns = [
    { 
      title: 'Nombre Cliente', 
      dataIndex: 'fullName', 
      key: 'fullName', 
      sorter: true,
      render: (_, record) => {
        return record.fullName || 'Sin nombre';
      }
    },
    { title: 'Email', dataIndex: 'email', key: 'email', sorter: true },
    { title: 'Rol', dataIndex: 'role', key: 'role' }, 
    {
      title: 'Estatus',
      dataIndex: 'isActive',
      key: 'status',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'volcano'}>
          {isActive ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
      // filters: [
      //   { text: 'Activo', value: '1' },
      //   { text: 'Inactivo', value: '0' },
      // ],
      // onFilter: (value, record) => String(record.is_active ? 1 : 0) === String(value),
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <Dropdown menu={getActionsMenu(record)} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: '16px' }}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={<Title level={3} style={{ marginBottom: 0 }}>Clientes</Title>}
        /*
        extra={
          <Tooltip title="Agregar cliente">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              aria-label="Agregar cliente"
              onClick={() => navigate('/clients/add')}
            />
          </Tooltip>
        }
        */
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space direction="horizontal" style={{ width: '100%' }}>
            <Input.Search
              placeholder="Buscar por nombre o email"
              allowClear
              onSearch={onSearch}
              style={{ maxWidth: 360 }}
            />
              <span>Rol:</span>
              <Select
                allowClear
                style={{ minWidth: 360 }}
                options={SPLASH_ROLES_OPTIONS}
                value={roleId}
                onChange={v => onChangeRole(v)}
              />
          </Space>
          
          <Table
            rowKey={(r) => r.id ?? r._id}
            columns={columns}
            dataSource={data}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
            }}
            onChange={onChange}
          />
        </Space>
      </Card>
    </div>
  );
};

export default ClientsList;
